import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Form from 'react-bootstrap/Form';
import LanguageList from '../LanguageList/LanguageList';
import SearchInterpreter from '../SearchInterpreter/SearchInterpreter';
import {
  INTERPRETER_VIDEO,
  INTERPRETER_AUDIO,
  SELECT_INTERPRETER_VIDEO,
  SELECT_INVITE_AUDIO_ONLY_PARTS,
  NCAL_REGION,
  SCAL_REGION
} from '../../constants/interpreter.constants';
import DialPadContainer from '../DialPadContainer/DialPadContainer';
import { CONTEXT_ADD_INTERPRETER } from '../../constants/dialpad.constants';
import { getRadioButtonsForInterpreter } from '../../helper/commonUtils';
import { STORE_NAMES } from '../../constants/store.constants';
import ServiceAreaList from '../ServiceAreaList/ServiceAreaList';
import InterpreterJoinedList from '../InterpreterJoinedList/InterpreterJoinedList';
import { VISIT_TYPES } from '../../constants/app.constants';
import AslLanguage from '../AslLanguage/AslLanguage';
import InterpreterAction from '../../store/actions/InterpreterAction';

const AddInterpreter = () => {
  const { RadioButtons } = getRadioButtonsForInterpreter();
  const interpreterDefaultValue = INTERPRETER_VIDEO;
  const [interpreterType, setInterpreterType] = useState(interpreterDefaultValue);
  const { meetingID: zoomMeetingId } = useSelector(state => state?.[STORE_NAMES.ZOOM_CLIENT]);
  const { appointmentRegion: region, appointmentVisitType: visitType } = useSelector(state => state?.[STORE_NAMES.APPOINTMENT_DETAILS]);
  const interpreterEventsPolling = useSelector(state => state?.[STORE_NAMES.APP]?.interpreterEventsPolling);
  const { enabled = false } = interpreterEventsPolling || {};
  // Select correct lists from Redux
  const videoJoinedInterpretersList = useSelector(state => state?.[STORE_NAMES.INTERPRETER]?.videoJoinedInterpretersList || []);
  const audioJoinedInterpretersList = useSelector(state => state?.[STORE_NAMES.INTERPRETER]?.audioJoinedInterpretersList || []);
  const dispatch = useDispatch();
  // Determine hasJoinedInterpreters based on active tab and region
  let hasJoinedInterpreters = false;
  if (region && enabled) {
    if (interpreterType === INTERPRETER_VIDEO) {
      hasJoinedInterpreters = videoJoinedInterpretersList.length > 0;
    } else if (interpreterType === INTERPRETER_AUDIO) {
      hasJoinedInterpreters = audioJoinedInterpretersList.length > 0;
    }
  }
  const onInterpreterTypeSelect = event => {
    const selectedType = event.target.value;
    setInterpreterType(selectedType);
    dispatch(InterpreterAction.setInterpreterType(selectedType));
  };
  const isGvVisitType = visitType === VISIT_TYPES.GV;
  const compactVideo = videoJoinedInterpretersList.length > 0;
  const compactAudio = audioJoinedInterpretersList.length > 0;
  const isScalAudioRegion = region === SCAL_REGION;
  const inviteButtonState = useSelector(state => state?.[STORE_NAMES.INVITE_BUTTON] || {});
  const sessionID = inviteButtonState.sessionID || null;

  const inviteAudioOnlyMessage = (
    <div>
      {SELECT_INVITE_AUDIO_ONLY_PARTS.before}
      <span className="bold-text">{SELECT_INVITE_AUDIO_ONLY_PARTS.bold}</span>
      {SELECT_INVITE_AUDIO_ONLY_PARTS.after}
    </div>
  );

  useEffect(() => {
    if (region && interpreterType === INTERPRETER_AUDIO) {
      dispatch(InterpreterAction.setInterpreterType(INTERPRETER_AUDIO));
    }
  }, [region, interpreterType, dispatch]);

  return (
    <div className="add-interpreter-container">
      {!isGvVisitType && (
        <Form>
          <div key="default-radio" className="p-3">
            {RadioButtons.map((radioButton, index) => {
              const btnIndex = `radio-${index}`;
              return (
                <Form.Check
                  key={btnIndex}
                  type="radio"
                  label={radioButton.label}
                  name={radioButton.name}
                  value={radioButton.value}
                  defaultChecked={radioButton.checked}
                  onChange={onInterpreterTypeSelect}
                  inline
                />
              );
            })}
          </div>
        </Form>
      )}

      {interpreterType === INTERPRETER_VIDEO && !isGvVisitType && (
        <>
          <p>{SELECT_INTERPRETER_VIDEO}</p>
          <SearchInterpreter interpreterType={INTERPRETER_VIDEO}></SearchInterpreter>
          <LanguageList interpreterType={INTERPRETER_VIDEO} compact={hasJoinedInterpreters} />
          {region && enabled && sessionID && (
            <>
              {compactVideo && <hr className="row vve-padding-top" />}
              <InterpreterJoinedList region={region} zoomMeetingId={zoomMeetingId} interpreterType={INTERPRETER_VIDEO} compact={compactVideo} />
            </>
          )}
        </>
      )}

      {interpreterType === INTERPRETER_AUDIO && !isGvVisitType && (
        <>
          {region !== NCAL_REGION && region !== SCAL_REGION && (
            <>
              {/* Audio-only flow: hide keypad and show invite message for non-NCAL/non-SCAL regions. */}
              {inviteAudioOnlyMessage}
            </>
          )}
          {region === SCAL_REGION && (
            <>
              <ServiceAreaList interpreterType={INTERPRETER_AUDIO} compact={hasJoinedInterpreters}></ServiceAreaList>
              <DialPadContainer context={CONTEXT_ADD_INTERPRETER}></DialPadContainer>
            </>
          )}
          {region === NCAL_REGION && <>{inviteAudioOnlyMessage}</>}
          {region && enabled && sessionID && (
            <>
              {/* Only show these when compactAudio is true */}
              {compactAudio && (
                <>
                  {!isScalAudioRegion && <div className="vve-padding-top-add-interpreter"></div>}
                  <hr className="row vve-padding-top" />
                </>
              )}
              <InterpreterJoinedList region={region} zoomMeetingId={zoomMeetingId} interpreterType={INTERPRETER_AUDIO} compact={compactAudio} />
            </>
          )}
        </>
      )}

      {isGvVisitType && (
        <>
          <AslLanguage></AslLanguage>
          <LanguageList interpreterType={INTERPRETER_VIDEO}></LanguageList>
        </>
      )}
    </div>
  );
};

export default AddInterpreter;
